// src/domain/money.ts
var MONEDA = "MXN";
function pesosACentavos(pesos) {
  return Math.round(pesos * 100);
}
function centavosAPesos(centavos) {
  return centavos / 100;
}
function formatearMXN(centavos, opciones) {
  const texto = new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 2
  }).format(centavosAPesos(centavos));
  return opciones?.conCodigo ? `${texto} MXN` : texto;
}
function aplicarPorcentaje(centavos, porcentaje) {
  return Math.round(centavos * porcentaje / 100);
}
function restarSinNegativo(total, resta) {
  return Math.max(0, total - resta);
}
function esCentavosValidos(valor) {
  return typeof valor === "number" && Number.isInteger(valor) && valor >= 0 && Number.isFinite(valor);
}

// src/domain/types.ts
var DISCIPLINAS = ["ZYCLING", "ZTRENGTH", "ZCULPT", "ZYNC", "ZAVAGE", "ESPECIAL"];
var ESTADOS_CLASE = ["activa", "cancelada", "finalizada"];
var TIPOS_CREDITO = [
  "clase",
  "zycling_unlimited",
  "ztrength_unlimited",
  "combo_unlimited",
  "bday_deal"
];
var TIPOS_UNLIMITED = [
  "zycling_unlimited",
  "ztrength_unlimited",
  "combo_unlimited"
];
function esUnlimited(tipo) {
  return TIPOS_UNLIMITED.includes(tipo);
}
var ESTADOS_COMPRA = ["activo", "expirado", "agotado", "reembolsado"];
var ROLES = ["alumno", "coach", "recepcion", "admin"];
var ROLES_STAFF = ["coach", "recepcion", "admin"];
function esStaff(rol) {
  return ROLES_STAFF.includes(rol);
}
var ESTADOS_USUARIO = ["activo", "restricted", "inactivo", "pre-registro"];
function esPlanoReutilizable(l) {
  return !l.dedicado_a;
}
var ESTADOS_RESERVA = ["reservado", "cancelado"];
var ESTADOS_ASISTENCIA = ["pendiente", "asistio", "falta"];
var TIPOS_DESCUENTO = ["porcentaje", "monto_fijo"];

// src/domain/constants.ts
var COL = {
  USUARIOS: "usuarios",
  PAQUETES: "paquetes_tienda",
  CLASES: "clases_calendario",
  RESERVACIONES: "reservaciones",
  COMPRAS: "compras_paquetes",
  ORDENES: "ordenes_tienda",
  MULTAS: "multas",
  CUPONES: "cupones_descuento",
  /** Espejo de lectura pública: solo datos de promoción, nunca segmentación. */
  CUPONES_PUBLICOS: "cupones_publicos",
  USOS_CUPONES: "usos_cupones",
  MERCH: "inventario_merch",
  /** Vitrina pública del staff: solo datos de promoción, nunca PII. */
  COACHES: "coaches",
  INSTALACIONES: "instalaciones_galeria",
  SOLICITUDES_BDAY: "solicitudes_bday",
  CONFIGURACIONES: "configuraciones",
  LAYOUTS: "layouts_salas",
  /**
   * Cobros que la pasarela hizo y el sistema nunca acreditó.
   *
   * Los escribe solo `conciliarCobros`. Es la única parte del diagnóstico que se
   * guarda en la base, porque es dinero de una persona y alguien tiene que
   * resolverlo; el resto va a los registros y caduca solo.
   */
  COBROS_SIN_ACREDITAR: "cobros_sin_acreditar",
  /** Subcolección bajo usuarios/{uid} */
  NOTIFICACIONES: "notificaciones"
};
var CONFIG_DOC = {
  OPERATIVA: "operativa",
  /**
   * Catálogo de modalidades.
   *
   * Documento nuevo y no una reescritura de `clases_config`, que guardaba la
   * forma vieja —`{id, tipo_id, nombre}` agrupada por un array `tipos` sin
   * entrada para ZCULPT—. Con documento aparte, «¿ya se migró?» es comprobar si
   * existe, la migración se puede simular cuantas veces se quiera sin tocar lo
   * anterior, y volver atrás es dejar de leerlo.
   */
  MODALIDADES: "modalidades",
  /** Forma vieja del catálogo. Se lee solo mientras no se migre. */
  CLASES: "clases_config",
  GENERAL: "general",
  TERMINOS: "terminos_condiciones",
  LANDING: "landing",
  /**
   * Interruptor de la analítica de producto. `{ activa: boolean }`.
   *
   * Vive en la base y no en una variable de compilación **a propósito**.
   * Encenderla no es una decisión técnica: registrar el comportamiento de
   * personas exige un aviso de privacidad por la LFPDPPP, y eso lo aprueba la
   * dueña del negocio. Un interruptor que se mueve sin desplegar deja esa
   * decisión donde corresponde y con constancia de cuándo se tomó.
   *
   * Ausente o `false` significa apagada, que es el estado por omisión.
   */
  ANALITICA: "analitica"
};
var MS_POR_DIA = 24 * 60 * 60 * 1e3;
var MS_POR_HORA = 60 * 60 * 1e3;
var CONFIG_OPERATIVA_DEFAULT = {
  cancelacion_horas_reembolso: 6,
  faltas_para_restriccion: 2,
  multa_acumulacion_centavos: pesosACentavos(100),
  multa_ilimitado_centavos: pesosACentavos(200),
  dias_por_ventana_invitacion: 30,
  max_reservas_activas: 0
};
var ERR = {
  NO_AUTENTICADO: "no_autenticado",
  SIN_PERMISOS: "sin_permisos",
  CUENTA_RESTRINGIDA: "cuenta_restringida",
  CUENTA_INACTIVA: "cuenta_inactiva",
  SIN_CREDITOS: "sin_creditos",
  PAQUETE_EXPIRADO: "paquete_expirado",
  PAQUETE_NO_CUBRE_DISCIPLINA: "paquete_no_cubre_disciplina",
  LUGAR_OCUPADO: "lugar_ocupado",
  LUGAR_INEXISTENTE: "lugar_inexistente",
  LUGAR_FUERA_DE_SERVICIO: "lugar_fuera_de_servicio",
  CLASE_LLENA: "clase_llena",
  CLASE_NO_ENCONTRADA: "clase_no_encontrada",
  CLASE_YA_INICIO: "clase_ya_inicio",
  CLASE_CANCELADA: "clase_cancelada",
  RESERVA_DUPLICADA: "reserva_duplicada",
  RESERVA_NO_ENCONTRADA: "reserva_no_encontrada",
  RESERVA_NO_ACTIVA: "reserva_no_activa",
  SIN_INVITACIONES: "sin_invitaciones",
  INVITADO_MISMO_LUGAR: "invitado_mismo_lugar",
  CUPON_INEXISTENTE: "cupon_inexistente",
  CUPON_INACTIVO: "cupon_inactivo",
  CUPON_FUERA_DE_VIGENCIA: "cupon_fuera_de_vigencia",
  CUPON_AGOTADO: "cupon_agotado",
  CUPON_LIMITE_USUARIO: "cupon_limite_usuario",
  CUPON_NO_APLICABLE: "cupon_no_aplicable",
  CUPON_PERFIL_NO_ELEGIBLE: "cupon_perfil_no_elegible",
  PAQUETE_SOLO_PRIMERA_VEZ: "paquete_solo_primera_vez",
  PAQUETE_NO_DISPONIBLE: "paquete_no_disponible",
  BDAY_SIN_FECHA_NACIMIENTO: "bday_sin_fecha_nacimiento",
  BDAY_FUERA_DE_MES: "bday_fuera_de_mes",
  BDAY_SALA_YA_USADA: "bday_sala_ya_usada",
  BDAY_ANTICIPACION_INSUFICIENTE: "bday_anticipacion_insuficiente",
  BDAY_COACHES_INVALIDOS: "bday_coaches_invalidos",
  STOCK_INSUFICIENTE: "stock_insuficiente",
  CARRITO_VACIO: "carrito_vacio",
  PAGO_RECHAZADO: "pago_rechazado",
  DATOS_INVALIDOS: "datos_invalidos",
  SESION_CADUCADA: "sesion_caducada",
  ROL_SIN_CAMBIO: "rol_sin_cambio",
  NO_PUEDE_CAMBIAR_SU_ROL: "no_puede_cambiar_su_rol",
  ULTIMO_ADMIN: "ultimo_admin"
};
var MENSAJES_ERROR = {
  [ERR.NO_AUTENTICADO]: "Necesitas iniciar sesi\xF3n para continuar.",
  [ERR.SIN_PERMISOS]: "No tienes permisos para realizar esta acci\xF3n.",
  [ERR.CUENTA_RESTRINGIDA]: "Tu cuenta est\xE1 restringida por una multa pendiente. Liqu\xEDdala para volver a reservar.",
  [ERR.CUENTA_INACTIVA]: "Tu cuenta est\xE1 inactiva. Contacta a recepci\xF3n para reactivarla.",
  [ERR.SIN_CREDITOS]: "No tienes cr\xE9ditos disponibles. Compra un paquete para reservar.",
  [ERR.PAQUETE_EXPIRADO]: "Tu paquete venci\xF3. Compra uno nuevo para seguir entrenando.",
  [ERR.PAQUETE_NO_CUBRE_DISCIPLINA]: "Tu paquete no incluye esta disciplina.",
  [ERR.LUGAR_OCUPADO]: "Ese lugar acaba de ser tomado. Elige otro.",
  [ERR.LUGAR_INEXISTENTE]: "Ese lugar no existe en esta sala.",
  [ERR.LUGAR_FUERA_DE_SERVICIO]: "Ese lugar est\xE1 temporalmente fuera de servicio.",
  [ERR.CLASE_LLENA]: "La clase est\xE1 llena.",
  [ERR.CLASE_NO_ENCONTRADA]: "No encontramos esa clase.",
  [ERR.CLASE_YA_INICIO]: "La clase ya comenz\xF3.",
  [ERR.CLASE_CANCELADA]: "Esta clase fue cancelada.",
  [ERR.RESERVA_DUPLICADA]: "Ya tienes un lugar apartado en esta clase.",
  [ERR.RESERVA_NO_ENCONTRADA]: "No encontramos esa reservaci\xF3n.",
  [ERR.RESERVA_NO_ACTIVA]: "Esa reservaci\xF3n ya no est\xE1 activa.",
  [ERR.SIN_INVITACIONES]: "No tienes pases de invitado disponibles en este periodo.",
  [ERR.INVITADO_MISMO_LUGAR]: "T\xFA y tu invitado no pueden ocupar el mismo lugar.",
  [ERR.CUPON_INEXISTENTE]: "Ese c\xF3digo de cup\xF3n no existe.",
  [ERR.CUPON_INACTIVO]: "Ese cup\xF3n ya no est\xE1 activo.",
  [ERR.CUPON_FUERA_DE_VIGENCIA]: "Ese cup\xF3n no es v\xE1lido en esta fecha.",
  [ERR.CUPON_AGOTADO]: "Ese cup\xF3n agot\xF3 sus usos disponibles.",
  [ERR.CUPON_LIMITE_USUARIO]: "Ya usaste este cup\xF3n el m\xE1ximo de veces permitido.",
  [ERR.CUPON_NO_APLICABLE]: "El cup\xF3n no aplica a los art\xEDculos de tu carrito.",
  [ERR.CUPON_PERFIL_NO_ELEGIBLE]: "Este cup\xF3n no aplica a tu perfil.",
  [ERR.PAQUETE_SOLO_PRIMERA_VEZ]: "Este paquete es exclusivo para tu primera compra.",
  [ERR.PAQUETE_NO_DISPONIBLE]: "Ese paquete no est\xE1 disponible.",
  [ERR.BDAY_SIN_FECHA_NACIMIENTO]: "Agrega tu fecha de nacimiento en tu perfil para ver tus Bday Dealz.",
  [ERR.BDAY_FUERA_DE_MES]: "Los Bday Dealz solo est\xE1n disponibles durante el mes de tu cumplea\xF1os.",
  [ERR.BDAY_SALA_YA_USADA]: "Ya tienes un Bday Deal de esta sala este a\xF1o.",
  [ERR.BDAY_ANTICIPACION_INSUFICIENTE]: "Los eventos se agendan con al menos 14 d\xEDas de anticipaci\xF3n. Elige una fecha m\xE1s adelante.",
  [ERR.BDAY_COACHES_INVALIDOS]: "Elige uno o dos coaches para tu evento.",
  [ERR.STOCK_INSUFICIENTE]: "No hay suficiente inventario de ese art\xEDculo.",
  [ERR.CARRITO_VACIO]: "Tu carrito est\xE1 vac\xEDo.",
  [ERR.PAGO_RECHAZADO]: "El pago fue rechazado. Verifica tu m\xE9todo de pago.",
  [ERR.DATOS_INVALIDOS]: "Los datos enviados no son v\xE1lidos.",
  [ERR.SESION_CADUCADA]: "Tus permisos cambiaron. Vuelve a intentarlo; si contin\xFAa, cierra sesi\xF3n y entra de nuevo.",
  [ERR.ROL_SIN_CAMBIO]: "Esa persona ya tiene ese rol.",
  [ERR.NO_PUEDE_CAMBIAR_SU_ROL]: "No puedes cambiar tu propio rol. P\xEDdeselo a otro administrador.",
  [ERR.ULTIMO_ADMIN]: "Es el \xFAnico administrador. Nombra a otro antes de quitarle el rol."
};
function mensajeDe(codigo) {
  return MENSAJES_ERROR[codigo] ?? "Ocurri\xF3 un error inesperado.";
}

// src/domain/schemas.ts
import { z } from "zod";

// src/rules/lesiones.ts
var LONGITUD_MINIMA_LESION = 10;
var NO_APORTAN = /* @__PURE__ */ new Set([
  "ninguna",
  "ninguno",
  "nada",
  "no tengo",
  "no aplica",
  "sin lesion",
  "sin lesi\xF3n",
  "n/a",
  "na"
]);
function descripcionEsUtil(descripcion) {
  const limpia = descripcion.trim();
  if (limpia.length < LONGITUD_MINIMA_LESION) return false;
  const normalizada = limpia.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[.\-_/\s]+/g, " ").trim();
  return !NO_APORTAN.has(normalizada);
}
function tieneLesionReportada(perfil) {
  if (!perfil?.lesion_activa) return false;
  return descripcionEsUtil(perfil.lesion_descripcion ?? "");
}
function antiguedadReporteDias(perfil, ahoraMs) {
  if (!perfil.lesion_actualizada_ms) return null;
  return Math.max(0, Math.floor((ahoraMs - perfil.lesion_actualizada_ms) / MS_POR_DIA));
}
function antiguedadLegible(dias) {
  if (dias === null) return "sin fecha de actualizaci\xF3n";
  if (dias === 0) return "actualizada hoy";
  if (dias === 1) return "actualizada ayer";
  if (dias < 30) return `actualizada hace ${dias} d\xEDas`;
  if (dias < 60) return "actualizada hace un mes";
  if (dias < 365) return `actualizada hace ${Math.floor(dias / 30)} meses`;
  return "actualizada hace m\xE1s de un a\xF1o";
}
function reporteEsAntiguo(dias, umbralDias = 180) {
  return dias !== null && dias >= umbralDias;
}

// src/rules/bday.ts
var OFFSET_MEXICO_HORAS = -6;
var ANTICIPACION_MINIMA_DIAS = 14;
var MIN_COACHES_BDAY = 1;
var MAX_COACHES_BDAY = 2;
var SALAS_BDAY = ["ride", "ztrength"];
function enHoraLocal(ahoraMs) {
  return new Date(ahoraMs + OFFSET_MEXICO_HORAS * 60 * 60 * 1e3);
}
function mesLocal(ahoraMs) {
  return enHoraLocal(ahoraMs).getUTCMonth() + 1;
}
function anioLocal(ahoraMs) {
  return enHoraLocal(ahoraMs).getUTCFullYear();
}
function mesDeNacimiento(fechaNacimientoISO) {
  if (!fechaNacimientoISO || fechaNacimientoISO.length < 7) return null;
  const mes = Number(fechaNacimientoISO.slice(5, 7));
  return Number.isInteger(mes) && mes >= 1 && mes <= 12 ? mes : null;
}
function esMesDeCumpleanos(fechaNacimientoISO, ahoraMs) {
  const mes = mesDeNacimiento(fechaNacimientoISO);
  return mes !== null && mes === mesLocal(ahoraMs);
}
function salaYaUsadaEsteAnio(previas, sala, ahoraMs) {
  const anio = anioLocal(ahoraMs);
  return previas.some((p) => p.sala === sala && p.anio === anio && p.estado !== "rechazada");
}
function evaluarDisponibilidadBday(ctx) {
  const conExcepcion = Boolean(ctx.motivo_excepcion && ctx.motivo_excepcion.trim().length >= 5);
  if (!conExcepcion) {
    if (!ctx.fecha_nacimiento) return { ok: false, codigo: ERR.BDAY_SIN_FECHA_NACIMIENTO };
    if (!esMesDeCumpleanos(ctx.fecha_nacimiento, ctx.ahora_ms)) {
      return { ok: false, codigo: ERR.BDAY_FUERA_DE_MES };
    }
    if (salaYaUsadaEsteAnio(ctx.solicitudes_previas, ctx.sala, ctx.ahora_ms)) {
      return { ok: false, codigo: ERR.BDAY_SALA_YA_USADA };
    }
  }
  return { ok: true, por_excepcion: conExcepcion };
}
function fechaMinimaEvento(ahoraMs, anticipacionDias = ANTICIPACION_MINIMA_DIAS) {
  const limite = enHoraLocal(ahoraMs + anticipacionDias * MS_POR_DIA);
  return limite.toISOString().slice(0, 10);
}
function validarSolicitudBday(params) {
  const anticipacion = params.anticipacion_dias ?? ANTICIPACION_MINIMA_DIAS;
  if (params.coaches.length < MIN_COACHES_BDAY || params.coaches.length > MAX_COACHES_BDAY) {
    return { ok: false, codigo: ERR.BDAY_COACHES_INVALIDOS };
  }
  if (new Set(params.coaches).size !== params.coaches.length) {
    return { ok: false, codigo: ERR.BDAY_COACHES_INVALIDOS };
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(params.fecha_evento)) {
    return { ok: false, codigo: ERR.DATOS_INVALIDOS };
  }
  if (params.fecha_evento < fechaMinimaEvento(params.ahora_ms, anticipacion)) {
    return { ok: false, codigo: ERR.BDAY_ANTICIPACION_INSUFICIENTE };
  }
  return { ok: true };
}
function salasDisponibles(previas, ahoraMs) {
  return SALAS_BDAY.filter((sala) => !salaYaUsadaEsteAnio(previas, sala, ahoraMs));
}

// src/domain/schemas.ts
var idFirestore = z.string().trim().min(1).max(1500);
var emailNormalizado = z.string().trim().toLowerCase().email().max(254);
var nombrePersona = z.string().trim().min(1).max(80);
var telefono = z.string().trim().max(25).optional().default("");
var fechaISO = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Formato esperado YYYY-MM-DD");
var horaISO = z.string().regex(/^\d{2}:\d{2}$/, "Formato esperado HH:MM");
var numeroLugar = z.number().int().positive().max(500);
var datosInvitadoSchema = z.object({
  nombre: nombrePersona,
  apellido: z.string().trim().max(80).default(""),
  email: emailNormalizado,
  telefono
});
var reservarClaseSchema = z.object({
  clase_id: idFirestore,
  numero_lugar: numeroLugar,
  con_invitado: z.boolean().default(false),
  lugar_invitado: numeroLugar.optional(),
  datos_invitado: datosInvitadoSchema.optional()
}).refine((d) => !d.con_invitado || d.lugar_invitado != null && d.datos_invitado != null, {
  message: "Una reserva con invitado requiere su lugar y sus datos.",
  path: ["datos_invitado"]
}).refine((d) => !d.con_invitado || d.lugar_invitado !== d.numero_lugar, {
  message: "El invitado no puede ocupar el mismo lugar que el titular.",
  path: ["lugar_invitado"]
});
var cancelarReservaSchema = z.object({
  reservacion_id: idFirestore
});
var cambiarLugarSchema = z.object({
  reservacion_id: idFirestore,
  nuevo_lugar: numeroLugar,
  nuevo_lugar_invitado: numeroLugar.optional()
});
var lineaCarritoSchema = z.object({
  tipo: z.enum(["paquete", "merch"]),
  item_id: idFirestore,
  cantidad: z.number().int().positive().max(20),
  bday_config: z.object({
    pago_modalidad: z.enum(["50", "100"]),
    fecha: fechaISO,
    hora: horaISO,
    // Uno o dos coaches, como en la operación real del estudio.
    coaches: z.array(nombrePersona).min(MIN_COACHES_BDAY).max(MAX_COACHES_BDAY),
    /** Solo lo respeta el backend si quien llama es staff. */
    motivo_excepcion: z.string().trim().min(5).max(200).optional()
  }).optional()
});
var checkoutSchema = z.object({
  lineas: z.array(lineaCarritoSchema).min(1).max(30),
  cupon_codigo: z.string().trim().toUpperCase().max(40).optional(),
  metodo_entrega: z.enum(["digital", "recoger_en_estudio", "envio"]).default("digital"),
  /**
   * Referencia del intento de pago que devuelve la pasarela. Nunca se reciben
   * datos de tarjeta: el PAN jamás debe llegar al backend.
   */
  payment_intent_id: z.string().trim().min(1).max(200).optional()
});
var validarCuponSchema = z.object({
  codigo: z.string().trim().toUpperCase().min(1).max(40),
  lineas: z.array(lineaCarritoSchema).min(1).max(30)
});
var crearStaffSchema = z.object({
  email: emailNormalizado,
  password: z.string().min(10).max(128),
  rol: z.enum(ROLES).refine((r) => r !== "alumno", "Use el registro p\xFAblico para alumnos."),
  nombre: nombrePersona,
  apellido: z.string().trim().max(80).default(""),
  especialidad: z.string().trim().max(120).default(""),
  biografia: z.string().trim().max(2e3).default(""),
  imagen: z.string().trim().max(500).default(""),
  fecha_nacimiento: fechaISO.optional(),
  fecha_ingreso: fechaISO.optional()
});
var cambiarRolSchema = z.object({
  usuario_id: idFirestore,
  rol: z.enum(ROLES),
  motivo: z.string().trim().min(5).max(200)
});
var accesosStaffSchema = z.object({
  usuario_ids: z.array(idFirestore).min(1).max(100)
});
var marcarAsistenciaSchema = z.object({
  reservacion_id: idFirestore,
  asistencia: z.enum(["pendiente", "asistio", "falta"])
});
var asignarPaqueteSchema = z.object({
  usuario_id: idFirestore,
  paquete_id: idFirestore,
  cantidad: z.number().int().positive().max(10).default(1),
  motivo: z.string().trim().max(200).default("Asignaci\xF3n manual de staff")
});
var liquidarMultaSchema = z.object({
  multa_id: idFirestore,
  metodo: z.enum(["terminal_fisica", "efectivo", "transferencia", "en_linea"]),
  referencia: z.string().trim().max(120).default("")
});
var layoutSalaSchema = z.object({
  id: idFirestore,
  nombre: z.string().trim().min(1).max(80),
  disciplina: z.enum(DISCIPLINAS),
  filas: z.number().int().positive().max(20),
  columnas: z.number().int().positive().max(40),
  lugares: z.array(
    z.object({
      numero: numeroLugar,
      fila: z.number().int().positive().max(20),
      columna: z.number().int().positive().max(40),
      activo: z.boolean(),
      etiqueta: z.string().trim().max(30).optional()
    })
  ).min(1).max(500)
});
var paqueteSchema = z.object({
  nombre: z.string().trim().min(1).max(120),
  descripcion: z.string().trim().max(500).optional(),
  precio_centavos: z.number().int().nonnegative().max(1e7),
  total_clases: z.number().int().nonnegative().max(9999),
  vigencia_dias: z.number().int().positive().max(3650),
  tipo_credito: z.enum(TIPOS_CREDITO),
  solo_primera_vez: z.boolean().default(false),
  visible_en_tienda: z.boolean().default(true),
  activo: z.boolean().default(true),
  orden: z.number().int().nonnegative().default(0),
  invitaciones_por_periodo: z.number().int().nonnegative().max(10).default(0),
  sala: z.enum(["ride", "ztrength"]).optional(),
  spots: z.number().int().positive().max(200).optional(),
  brunch: z.boolean().optional()
});
var cuponSchema = z.object({
  codigo: z.string().trim().toUpperCase().min(3).max(40).regex(/^[A-Z0-9_-]+$/, "Solo letras, n\xFAmeros, guion y guion bajo."),
  tipo_descuento: z.enum(TIPOS_DESCUENTO),
  valor: z.number().positive(),
  paquetes_aplicables: z.array(idFirestore).max(100).default([]),
  fecha_inicio_ms: z.number().int().positive(),
  fecha_fin_ms: z.number().int().positive(),
  limite_global: z.number().int().nonnegative().max(1e6).default(0),
  limite_por_usuario: z.number().int().nonnegative().max(1e3).default(1),
  estado: z.enum(["activo", "inactivo"]).default("activo"),
  visible_publicamente: z.boolean().default(false),
  titulo_publico: z.string().trim().max(80).optional(),
  descripcion_publica: z.string().trim().max(240).optional(),
  restricciones: z.object({
    genero: z.enum(["todos", "femenino", "masculino", "otro"]).default("todos"),
    nuevos_usuarios: z.boolean().default(false),
    edad_minima: z.number().int().min(0).max(120).nullable().default(null),
    edad_maxima: z.number().int().min(0).max(120).nullable().default(null),
    clases_minimas_asistidas: z.number().int().min(0).max(1e4).nullable().default(null)
  }).optional()
}).refine((c) => c.fecha_fin_ms > c.fecha_inicio_ms, {
  message: "La fecha de fin debe ser posterior a la de inicio.",
  path: ["fecha_fin_ms"]
}).refine((c) => c.tipo_descuento !== "porcentaje" || c.valor <= 100, {
  message: "Un descuento porcentual no puede exceder 100.",
  path: ["valor"]
});
var notificacionSchema = z.object({
  titulo: z.string().trim().min(1).max(120),
  mensaje: z.string().trim().min(1).max(1e3),
  link: z.string().trim().max(300).optional(),
  filtros: z.object({
    rol: z.enum(["todos", ...ROLES]).default("todos"),
    estado: z.string().trim().max(20).default("todos"),
    genero: z.string().trim().max(20).default("todos"),
    mes_cumpleanos: z.string().regex(/^(todos|0[1-9]|1[0-2])$/).default("todos"),
    edad_minima: z.number().int().min(0).max(120).nullable().default(null),
    edad_maxima: z.number().int().min(0).max(120).nullable().default(null)
  }).optional(),
  target_emails: z.array(emailNormalizado).max(2e3).optional()
});
var asegurarPerfilSchema = z.object({
  nombre: z.string().trim().max(80).optional(),
  apellido: z.string().trim().max(80).optional()
});
var perfilAlumnoSchema = z.object({
  nombre: nombrePersona,
  apellido: z.string().trim().max(80).default(""),
  telefono,
  fecha_nacimiento: fechaISO.nullable().optional(),
  genero: z.enum(["femenino", "masculino", "otro", "prefiero_no_decir"]).optional(),
  lesion_activa: z.boolean().default(false),
  lesion_descripcion: z.string().trim().max(500).default("")
}).refine((p) => !p.lesion_activa || descripcionEsUtil(p.lesion_descripcion), {
  message: "Describe la lesi\xF3n con al menos 10 caracteres para que el coach sepa c\xF3mo guiarte, o desmarca la casilla.",
  path: ["lesion_descripcion"]
});

// src/rules/vigencia.ts
function calcularExpiracionMs(fechaCompraMs, vigenciaDias) {
  return fechaCompraMs + vigenciaDias * MS_POR_DIA;
}
function estaVigente(compra, ahoraMs) {
  return compra.estatus === "activo" && compra.fecha_expiracion_ms > ahoraMs;
}
function diasRestantes(fechaExpiracionMs, ahoraMs) {
  return Math.max(0, Math.ceil((fechaExpiracionMs - ahoraMs) / MS_POR_DIA));
}
function estatusCalculado(compra, ahoraMs) {
  if (compra.estatus === "reembolsado") return "reembolsado";
  if (compra.fecha_expiracion_ms <= ahoraMs) return "expirado";
  if (compra.tipo_credito === "clase" && compra.clases_restantes <= 0) return "agotado";
  return "activo";
}
function creditosUtilizables(compras, ahoraMs) {
  return compras.filter((c) => c.tipo_credito === "clase" && estaVigente(c, ahoraMs)).reduce((total, c) => total + Math.max(0, c.clases_restantes), 0);
}

// src/rules/invitaciones.ts
function ventanasTotales(vigenciaDias, diasPorVentana = CONFIG_OPERATIVA_DEFAULT.dias_por_ventana_invitacion) {
  if (diasPorVentana <= 0) return 0;
  return Math.floor(vigenciaDias / diasPorVentana);
}
function ventanaActual(fechaCompraMs, ahoraMs, diasPorVentana = CONFIG_OPERATIVA_DEFAULT.dias_por_ventana_invitacion) {
  if (ahoraMs < fechaCompraMs) return -1;
  if (diasPorVentana <= 0) return -1;
  const diasTranscurridos = Math.floor((ahoraMs - fechaCompraMs) / MS_POR_DIA);
  return Math.floor(diasTranscurridos / diasPorVentana);
}
function finDeVentanaMs(fechaCompraMs, ventana, diasPorVentana = CONFIG_OPERATIVA_DEFAULT.dias_por_ventana_invitacion) {
  return fechaCompraMs + (ventana + 1) * diasPorVentana * MS_POR_DIA;
}
function estadoInvitaciones(compra, ahoraMs, diasPorVentana = CONFIG_OPERATIVA_DEFAULT.dias_por_ventana_invitacion) {
  const vacio = {
    disponibles: 0,
    ventana: -1,
    ventanas_totales: 0,
    usados_en_ventana: 0,
    vence_ms: null
  };
  if (!esUnlimited(compra.tipo_credito)) return vacio;
  if (!estaVigente(compra, ahoraMs)) return vacio;
  const porPeriodo = Math.max(0, compra.invitaciones_por_periodo ?? 0);
  if (porPeriodo === 0) return vacio;
  const vigenciaDias = Math.round((compra.fecha_expiracion_ms - compra.fecha_compra_ms) / MS_POR_DIA);
  const totales = ventanasTotales(vigenciaDias, diasPorVentana);
  const ventana = ventanaActual(compra.fecha_compra_ms, ahoraMs, diasPorVentana);
  if (ventana < 0 || ventana >= totales) {
    return { ...vacio, ventanas_totales: totales, ventana };
  }
  const usados = compra.invitaciones_por_ventana?.[String(ventana)] ?? 0;
  return {
    disponibles: Math.max(0, porPeriodo - usados),
    ventana,
    ventanas_totales: totales,
    usados_en_ventana: usados,
    vence_ms: finDeVentanaMs(compra.fecha_compra_ms, ventana, diasPorVentana)
  };
}
function buscarCompraConInvitacion(compras, ahoraMs, diasPorVentana = CONFIG_OPERATIVA_DEFAULT.dias_por_ventana_invitacion) {
  const candidatas = compras.map((compra) => ({ compra, estado: estadoInvitaciones(compra, ahoraMs, diasPorVentana) })).filter((c) => c.estado.disponibles > 0).sort((a, b) => a.compra.fecha_expiracion_ms - b.compra.fecha_expiracion_ms);
  return candidatas[0] ?? null;
}
function registrarUsoInvitacion(invitacionesPorVentana, ventana) {
  const clave = String(ventana);
  return { ...invitacionesPorVentana, [clave]: (invitacionesPorVentana[clave] ?? 0) + 1 };
}
function devolverInvitacion(invitacionesPorVentana, ventana, fechaCompraMs, ahoraMs, diasPorVentana = CONFIG_OPERATIVA_DEFAULT.dias_por_ventana_invitacion) {
  const ventanaHoy = ventanaActual(fechaCompraMs, ahoraMs, diasPorVentana);
  const clave = String(ventana);
  const usados = invitacionesPorVentana[clave] ?? 0;
  if (ventanaHoy !== ventana || usados <= 0) {
    return { mapa: invitacionesPorVentana, devuelta: false };
  }
  return { mapa: { ...invitacionesPorVentana, [clave]: usados - 1 }, devuelta: true };
}

// src/rules/creditos.ts
var COBERTURA = {
  clase: ["ZYCLING", "ZTRENGTH", "ZCULPT", "ZYNC", "ZAVAGE", "ESPECIAL"],
  zycling_unlimited: ["ZYCLING"],
  ztrength_unlimited: ["ZTRENGTH", "ZCULPT", "ZYNC", "ZAVAGE"],
  combo_unlimited: ["ZYCLING", "ZTRENGTH", "ZCULPT", "ZYNC", "ZAVAGE"],
  bday_deal: []
};
function cubreDisciplina(tipo, disciplina) {
  return COBERTURA[tipo].includes(disciplina);
}
function seleccionarCredito(compras, disciplina, ahoraMs) {
  const vigentes = compras.filter((c) => estaVigente(c, ahoraMs));
  if (vigentes.length === 0) {
    return { ok: false, codigo: ERR.SIN_CREDITOS };
  }
  const unlimited = vigentes.filter((c) => esUnlimited(c.tipo_credito) && cubreDisciplina(c.tipo_credito, disciplina)).sort((a, b) => a.fecha_expiracion_ms - b.fecha_expiracion_ms);
  if (unlimited[0]) {
    return { ok: true, seleccion: { compra: unlimited[0], consumeCredito: false } };
  }
  const conCreditos = vigentes.filter((c) => c.tipo_credito === "clase" && c.clases_restantes > 0 && cubreDisciplina(c.tipo_credito, disciplina)).sort((a, b) => a.fecha_expiracion_ms - b.fecha_expiracion_ms);
  if (conCreditos[0]) {
    return { ok: true, seleccion: { compra: conCreditos[0], consumeCredito: true } };
  }
  const algunoCubre = vigentes.some((c) => cubreDisciplina(c.tipo_credito, disciplina));
  return { ok: false, codigo: algunoCubre ? ERR.SIN_CREDITOS : ERR.PAQUETE_NO_CUBRE_DISCIPLINA };
}
function resumirSaldo(compras, ahoraMs) {
  const vigentes = compras.filter((c) => estaVigente(c, ahoraMs));
  const creditos = vigentes.filter((c) => c.tipo_credito === "clase").reduce((total, c) => total + Math.max(0, c.clases_restantes), 0);
  const unlimited = vigentes.filter((c) => esUnlimited(c.tipo_credito)).map((c) => ({ tipo_credito: c.tipo_credito, expira_ms: c.fecha_expiracion_ms })).sort((a, b) => a.expira_ms - b.expira_ms);
  const porVencer = vigentes.filter((c) => c.tipo_credito === "clase" && c.clases_restantes > 0).map((c) => ({ cantidad: c.clases_restantes, expira_ms: c.fecha_expiracion_ms })).sort((a, b) => a.expira_ms - b.expira_ms);
  const fechas = vigentes.map((c) => c.fecha_expiracion_ms).sort((a, b) => a - b);
  return {
    creditos_disponibles: creditos,
    unlimited_activos: unlimited,
    proxima_expiracion_ms: fechas[0] ?? null,
    creditos_por_vencer: porVencer
  };
}

// src/rules/cancelacion.ts
function evaluarCancelacion(p) {
  const horasReembolso = p.horas_reembolso ?? CONFIG_OPERATIVA_DEFAULT.cancelacion_horas_reembolso;
  const horasRestantes = (p.clase_fecha_hora_ms - p.ahora_ms) / MS_POR_HORA;
  if (horasRestantes <= 0) {
    return {
      permitida: false,
      codigo: ERR.CLASE_YA_INICIO,
      reembolsa: false,
      early_cancel: false,
      horas_restantes: 0
    };
  }
  const early = horasRestantes > horasReembolso;
  return {
    permitida: true,
    reembolsa: early && !p.es_ilimitado,
    early_cancel: early,
    horas_restantes: horasRestantes
  };
}
function limiteCancelacionMs(claseFechaHoraMs, horasReembolso) {
  const horas = horasReembolso ?? CONFIG_OPERATIVA_DEFAULT.cancelacion_horas_reembolso;
  return claseFechaHoraMs - horas * MS_POR_HORA;
}

// src/rules/cupones.ts
function calcularEdad(fechaNacimientoISO, ahoraMs) {
  const nacimiento = /* @__PURE__ */ new Date(`${fechaNacimientoISO}T00:00:00Z`);
  if (Number.isNaN(nacimiento.getTime())) return null;
  const hoy = new Date(ahoraMs);
  let edad = hoy.getUTCFullYear() - nacimiento.getUTCFullYear();
  const mes = hoy.getUTCMonth() - nacimiento.getUTCMonth();
  if (mes < 0 || mes === 0 && hoy.getUTCDate() < nacimiento.getUTCDate()) {
    edad -= 1;
  }
  return edad;
}
function validarCupon(params) {
  const { cupon, ahora_ms, usos_del_usuario, perfil } = params;
  if (cupon.estado !== "activo") return { ok: false, codigo: ERR.CUPON_INACTIVO };
  if (ahora_ms < cupon.fecha_inicio_ms || ahora_ms > cupon.fecha_fin_ms) {
    return { ok: false, codigo: ERR.CUPON_FUERA_DE_VIGENCIA };
  }
  if (cupon.limite_global > 0 && cupon.usos_actuales >= cupon.limite_global) {
    return { ok: false, codigo: ERR.CUPON_AGOTADO };
  }
  if (cupon.limite_por_usuario > 0 && usos_del_usuario >= cupon.limite_por_usuario) {
    return { ok: false, codigo: ERR.CUPON_LIMITE_USUARIO };
  }
  const r = cupon.restricciones;
  if (r) {
    if (r.genero && r.genero !== "todos" && perfil.genero !== r.genero) {
      return { ok: false, codigo: ERR.CUPON_PERFIL_NO_ELEGIBLE };
    }
    if (r.nuevos_usuarios && perfil.compras_previas > 0) {
      return { ok: false, codigo: ERR.CUPON_PERFIL_NO_ELEGIBLE };
    }
    if (r.clases_minimas_asistidas != null && perfil.clases_asistidas < r.clases_minimas_asistidas) {
      return { ok: false, codigo: ERR.CUPON_PERFIL_NO_ELEGIBLE };
    }
    if (r.edad_minima != null || r.edad_maxima != null) {
      if (!perfil.fecha_nacimiento) return { ok: false, codigo: ERR.CUPON_PERFIL_NO_ELEGIBLE };
      const edad = calcularEdad(perfil.fecha_nacimiento, ahora_ms);
      if (edad == null) return { ok: false, codigo: ERR.CUPON_PERFIL_NO_ELEGIBLE };
      if (r.edad_minima != null && edad < r.edad_minima) {
        return { ok: false, codigo: ERR.CUPON_PERFIL_NO_ELEGIBLE };
      }
      if (r.edad_maxima != null && edad > r.edad_maxima) {
        return { ok: false, codigo: ERR.CUPON_PERFIL_NO_ELEGIBLE };
      }
    }
  }
  return { ok: true };
}
function tieneRestricciones(cupon) {
  const r = cupon.restricciones;
  if (!r) return false;
  return Boolean(
    r.genero && r.genero !== "todos" || r.nuevos_usuarios || r.edad_minima != null || r.edad_maxima != null || r.clases_minimas_asistidas != null
  );
}
function proyectarCuponPublico(cupon) {
  const etiquetaValor = cupon.tipo_descuento === "porcentaje" ? `${cupon.valor}% de descuento` : `${formatearMXN(cupon.valor)} de descuento`;
  return {
    cupon_id: cupon.cupon_id,
    codigo: cupon.codigo,
    titulo: cupon.titulo_publico?.trim() || etiquetaValor,
    descripcion: cupon.descripcion_publica?.trim() || "",
    tipo_descuento: cupon.tipo_descuento,
    valor: cupon.valor,
    fecha_fin_ms: cupon.fecha_fin_ms,
    tiene_restricciones: tieneRestricciones(cupon)
  };
}
function esPublicable(cupon, ahoraMs) {
  return cupon.visible_publicamente === true && cupon.estado === "activo" && ahoraMs <= cupon.fecha_fin_ms && (cupon.limite_global <= 0 || cupon.usos_actuales < cupon.limite_global);
}
function esItemElegible(cupon, itemId) {
  if (!cupon.paquetes_aplicables || cupon.paquetes_aplicables.length === 0) return true;
  return cupon.paquetes_aplicables.includes(itemId);
}
function calcularDescuento(cupon, itemsElegibles) {
  if (itemsElegibles.length === 0) {
    return { descuento_centavos: 0, base_centavos: 0, aplicado_a_paquete_unico: false };
  }
  const aplicaSoloAlMayor = cupon.limite_por_usuario === 1;
  const base = aplicaSoloAlMayor ? Math.max(...itemsElegibles.map((i) => i.precio_unitario_centavos)) : itemsElegibles.reduce((total, i) => total + i.precio_unitario_centavos * i.cantidad, 0);
  const descuento = cupon.tipo_descuento === "porcentaje" ? aplicarPorcentaje(base, cupon.valor) : Math.min(cupon.valor, base);
  return {
    descuento_centavos: Math.max(0, descuento),
    base_centavos: base,
    aplicado_a_paquete_unico: aplicaSoloAlMayor
  };
}

// src/rules/checkout.ts
function calcularCheckout(ctx) {
  if (!ctx.lineas.length) return { ok: false, codigo: ERR.CARRITO_VACIO };
  const lineas = [];
  const elegiblesCupon = [];
  let creditos = 0;
  for (const linea of ctx.lineas) {
    if (!Number.isInteger(linea.cantidad) || linea.cantidad <= 0) {
      return { ok: false, codigo: ERR.DATOS_INVALIDOS, detalle: "cantidad no entera o no positiva" };
    }
    if (linea.tipo === "paquete") {
      const paquete = ctx.catalogoPaquetes.get(linea.item_id);
      if (!paquete) return { ok: false, codigo: ERR.PAQUETE_NO_DISPONIBLE, detalle: linea.item_id };
      if (!paquete.activo) return { ok: false, codigo: ERR.PAQUETE_NO_DISPONIBLE, detalle: paquete.nombre };
      if (!paquete.visible_en_tienda && !ctx.esStaff) {
        return { ok: false, codigo: ERR.PAQUETE_NO_DISPONIBLE, detalle: paquete.nombre };
      }
      if (paquete.solo_primera_vez && ctx.paquetesYaComprados.has(paquete.id)) {
        return { ok: false, codigo: ERR.PAQUETE_SOLO_PRIMERA_VEZ, detalle: paquete.nombre };
      }
      if (paquete.tipo_credito === "bday_deal") {
        const cfg = linea.bday_config;
        if (!cfg) return { ok: false, codigo: ERR.DATOS_INVALIDOS, detalle: "falta la configuraci\xF3n del evento" };
        const excepcion = ctx.esStaff ? cfg.motivo_excepcion : void 0;
        const disponibilidad = evaluarDisponibilidadBday({
          fecha_nacimiento: ctx.fecha_nacimiento ?? null,
          sala: paquete.sala ?? "",
          solicitudes_previas: ctx.solicitudes_bday_previas ?? [],
          ahora_ms: ctx.ahora_ms,
          ...excepcion ? { motivo_excepcion: excepcion } : {}
        });
        if (!disponibilidad.ok) return { ok: false, codigo: disponibilidad.codigo, detalle: paquete.nombre };
        const solicitud = validarSolicitudBday({
          fecha_evento: cfg.fecha,
          coaches: cfg.coaches ?? [],
          ahora_ms: ctx.ahora_ms
        });
        if (!solicitud.ok) return { ok: false, codigo: solicitud.codigo, detalle: paquete.nombre };
        if (linea.cantidad !== 1) {
          return { ok: false, codigo: ERR.DATOS_INVALIDOS, detalle: "un evento se solicita de uno en uno" };
        }
      }
      const esApartado = paquete.tipo_credito === "bday_deal" && linea.bday_config?.pago_modalidad === "50";
      const unitario = esApartado ? Math.round(paquete.precio_centavos / 2) : paquete.precio_centavos;
      lineas.push({
        tipo: "paquete",
        item_id: paquete.id,
        nombre: paquete.nombre,
        cantidad: linea.cantidad,
        precio_unitario_centavos: unitario,
        subtotal_centavos: unitario * linea.cantidad
      });
      if (paquete.tipo_credito === "clase") {
        creditos += paquete.total_clases * linea.cantidad;
      }
      if (ctx.cupon && esItemElegible(ctx.cupon, paquete.id)) {
        elegiblesCupon.push({
          item_id: paquete.id,
          precio_unitario_centavos: unitario,
          cantidad: linea.cantidad
        });
      }
    } else {
      const merch = ctx.catalogoMerch.get(linea.item_id);
      if (!merch) return { ok: false, codigo: ERR.PAQUETE_NO_DISPONIBLE, detalle: linea.item_id };
      if (merch.stock < linea.cantidad) {
        return { ok: false, codigo: ERR.STOCK_INSUFICIENTE, detalle: merch.nombre };
      }
      lineas.push({
        tipo: "merch",
        item_id: merch.id,
        nombre: merch.nombre,
        cantidad: linea.cantidad,
        precio_unitario_centavos: merch.precio_centavos,
        subtotal_centavos: merch.precio_centavos * linea.cantidad
      });
    }
  }
  const subtotal = lineas.reduce((total, l) => total + l.subtotal_centavos, 0);
  let descuento = 0;
  if (ctx.cupon) {
    if (elegiblesCupon.length === 0) return { ok: false, codigo: ERR.CUPON_NO_APLICABLE };
    descuento = calcularDescuento(ctx.cupon, elegiblesCupon).descuento_centavos;
  }
  return {
    ok: true,
    resumen: {
      lineas,
      subtotal_centavos: subtotal,
      descuento_centavos: descuento,
      total_centavos: restarSinNegativo(subtotal, descuento),
      creditos_a_acreditar: creditos,
      cupon_codigo: ctx.cupon?.codigo ?? null
    }
  };
}

// src/rules/layout.ts
function generarLayout(params) {
  const { id, nombre, disciplina, filas, columnas } = params;
  const capacidad = params.capacidad ?? filas * columnas;
  const lugares = [];
  let numero = 1;
  for (let fila = 1; fila <= filas; fila++) {
    for (let columna = 1; columna <= columnas; columna++) {
      if (numero > capacidad) break;
      lugares.push({ numero, fila, columna, activo: true });
      numero++;
    }
  }
  return { id, nombre, disciplina, filas, columnas, lugares };
}
function capacidadUtil(layout) {
  return layout.lugares.filter((l) => l.activo).length;
}
function buscarLugar(layout, numero) {
  return layout.lugares.find((l) => l.numero === numero);
}
function validarLugar(params) {
  const lugar = buscarLugar(params.layout, params.numero);
  if (!lugar) return { ok: false, codigo: ERR.LUGAR_INEXISTENTE };
  if (!lugar.activo) return { ok: false, codigo: ERR.LUGAR_FUERA_DE_SERVICIO };
  if (params.ocupados.includes(params.numero)) return { ok: false, codigo: ERR.LUGAR_OCUPADO };
  return { ok: true };
}
function validarLugares(params) {
  const { layout, numeros, ocupados, capacidad_total } = params;
  if (new Set(numeros).size !== numeros.length) {
    return { ok: false, codigo: ERR.INVITADO_MISMO_LUGAR };
  }
  if (ocupados.length + numeros.length > capacidad_total) {
    return { ok: false, codigo: ERR.CLASE_LLENA };
  }
  for (const numero of numeros) {
    const r = validarLugar({ layout, numero, ocupados });
    if (!r.ok) return r;
  }
  return { ok: true };
}
function lugaresDisponibles(layout, ocupados) {
  return layout.lugares.filter((l) => l.activo && !ocupados.includes(l.numero)).map((l) => l.numero);
}

// src/rules/penalizaciones.ts
function evaluarPenalizacion(p) {
  const config = p.config ?? CONFIG_OPERATIVA_DEFAULT;
  const etiqueta = p.motivo === "no_show" ? "inasistencia" : "cancelaci\xF3n tard\xEDa";
  if (p.es_ilimitado) {
    return {
      faltas_acumuladas: p.faltas_previas,
      genera_multa: true,
      monto_centavos: config.multa_ilimitado_centavos,
      restringe_cuenta: true,
      descripcion: `Multa por ${etiqueta} con acceso ilimitado.`
    };
  }
  const faltas = p.faltas_previas + 1;
  if (faltas >= config.faltas_para_restriccion) {
    return {
      faltas_acumuladas: 0,
      genera_multa: true,
      monto_centavos: config.multa_acumulacion_centavos,
      restringe_cuenta: true,
      descripcion: `Multa por acumulaci\xF3n de ${faltas} faltas (\xFAltima: ${etiqueta}).`
    };
  }
  return {
    faltas_acumuladas: faltas,
    genera_multa: false,
    monto_centavos: 0,
    restringe_cuenta: false,
    descripcion: `Falta registrada por ${etiqueta}. Strike ${faltas} de ${config.faltas_para_restriccion}.`
  };
}
function puedeReactivarse(multasPendientes) {
  return multasPendientes === 0;
}

// src/rules/importacion.ts
var PREFIJOS = [
  ["ZYCLING", "ZYCLING"],
  ["ZTRENGTH", "ZTRENGTH"],
  ["ZCULPT", "ZCULPT"],
  ["ZYNC", "ZYNC"],
  ["ZAVAGE", "ZAVAGE"]
];
var SEPARADOR = /^\s*[:\-–—_+]*\s*/;
function despegarPrefijo(texto) {
  const limpio = texto.trim();
  const enMayusculas = limpio.toUpperCase();
  for (const [prefijo, disciplina] of PREFIJOS) {
    if (!enMayusculas.startsWith(prefijo)) continue;
    return { disciplina, resto: limpio.slice(prefijo.length).replace(SEPARADOR, "").trim() };
  }
  return null;
}
function deducirDisciplina(nombreClase) {
  const primero = despegarPrefijo(nombreClase);
  if (!primero) return "ESPECIAL";
  const segundo = despegarPrefijo(primero.resto);
  return segundo ? segundo.disciplina : primero.disciplina;
}
function extraerModalidad(nombreClase) {
  const primero = despegarPrefijo(nombreClase);
  if (!primero) return nombreClase.trim();
  const segundo = despegarPrefijo(primero.resto);
  const resto = segundo ? segundo.resto : primero.resto;
  return resto || nombreClase.trim();
}
function idDeterministaClase(inicioMs, salaId) {
  const d = new Date(inicioMs);
  const p = (n) => String(n).padStart(2, "0");
  const fecha = `${d.getUTCFullYear()}${p(d.getUTCMonth() + 1)}${p(d.getUTCDate())}`;
  const hora = `${p(d.getUTCHours())}${p(d.getUTCMinutes())}`;
  return `bst_${fecha}_${hora}_${salaId.slice(0, 8)}`;
}
function idDeterministaSala(nombreSala) {
  return `sala_${nombreSala.trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "")}`;
}
function convertirSala(sala, disciplina) {
  const reservables = sala.seats.filter((s) => !s.is_coach);
  const crudos = reservables.map((s) => {
    const numero = Number.parseInt(s.label, 10);
    return {
      numero: Number.isFinite(numero) ? numero : 0,
      fila: s.row,
      columna: s.col,
      activo: true,
      etiqueta: s.label
    };
  }).filter((l) => l.numero > 0).sort((a, b) => a.numero - b.numero);
  const desplazaFila = crudos.some((l) => l.fila === 0) ? 1 : 0;
  const desplazaColumna = crudos.some((l) => l.columna === 0) ? 1 : 0;
  const lugares = crudos.map((l) => ({
    ...l,
    fila: l.fila + desplazaFila,
    columna: l.columna + desplazaColumna
  }));
  return {
    id: idDeterministaSala(sala.name),
    nombre: sala.name,
    disciplina,
    filas: lugares.reduce((max, l) => Math.max(max, l.fila), 0),
    columnas: lugares.reduce((max, l) => Math.max(max, l.columna), 0),
    lugares
  };
}
function convertirSlots(slots) {
  const clases = [];
  const salas = /* @__PURE__ */ new Map();
  const descartados = [];
  const usoPorSala = /* @__PURE__ */ new Map();
  for (const slot of slots) {
    if (!slot.studio_class?.name) {
      descartados.push({ origen_id: slot.id, motivo: "sin nombre de clase" });
      continue;
    }
    if (!slot.room) {
      descartados.push({ origen_id: slot.id, motivo: "sin sala asignada" });
      continue;
    }
    const inicioMs = Date.parse(slot.start);
    if (!Number.isFinite(inicioMs)) {
      descartados.push({ origen_id: slot.id, motivo: `fecha inv\xE1lida: ${slot.start}` });
      continue;
    }
    const disciplina = deducirDisciplina(slot.studio_class.name);
    const sala = convertirSala(slot.room, disciplina);
    if (sala.lugares.length === 0) {
      descartados.push({ origen_id: slot.id, motivo: `la sala ${slot.room.name} no tiene asientos` });
      continue;
    }
    if (!salas.has(sala.id)) salas.set(sala.id, sala);
    const conteo = usoPorSala.get(sala.id) ?? /* @__PURE__ */ new Map();
    conteo.set(disciplina, (conteo.get(disciplina) ?? 0) + 1);
    usoPorSala.set(sala.id, conteo);
    const [principal, segundo] = slot.coaches;
    clases.push({
      clase_id: idDeterministaClase(inicioMs, slot.room.id),
      origen_id: slot.id,
      disciplina,
      modalidad: extraerModalidad(slot.studio_class.name),
      nombre_completo: slot.studio_class.name.trim(),
      coach_nombre: principal?.full_name?.trim() || "Coach",
      coach2_nombre: segundo?.full_name?.trim() ?? null,
      fecha_hora_ms: inicioMs,
      duracion_minutos: slot.minutes_duration || 50,
      // El cupo del turno manda sobre el de la sala: Boost permite abrir una
      // clase con menos lugares de los que tiene el plano.
      capacidad_total: Math.min(slot.people_limit || sala.lugares.length, sala.lugares.length),
      layout_id: sala.id
    });
  }
  const salasFinales = [...salas.values()].map((sala) => {
    const conteo = usoPorSala.get(sala.id);
    if (!conteo) return sala;
    const dominante = [...conteo.entries()].sort((a, b) => b[1] - a[1])[0];
    return dominante ? { ...sala, disciplina: dominante[0] } : sala;
  });
  return { clases, salas: salasFinales, descartados };
}
function inicioDeSemanaUTC(ms) {
  const d = new Date(ms);
  d.setUTCHours(0, 0, 0, 0);
  d.setUTCDate(d.getUTCDate() - (d.getUTCDay() + 6) % 7);
  return d.getTime();
}
var normalizarNombre = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
function buscarUno(nombre, directorio) {
  const buscado = normalizarNombre(nombre);
  if (!buscado) return null;
  const coincidencias = directorio.filter((c) => {
    const solo = normalizarNombre(c.nombre);
    const completo = normalizarNombre(`${c.nombre} ${c.apellido ?? ""}`);
    return solo === buscado || completo === buscado;
  });
  return coincidencias.length === 1 ? coincidencias[0].uid : null;
}
function resolverCoaches(coachNombre, coach2Nombre, directorio) {
  const partes = coachNombre.split(/\s+x\s+/i).map((p) => p.trim()).filter(Boolean);
  if (partes.length > 1 && !coach2Nombre) {
    return {
      coach_id: buscarUno(partes[0], directorio),
      coach2_id: buscarUno(partes[1], directorio)
    };
  }
  return {
    coach_id: buscarUno(coachNombre, directorio),
    coach2_id: coach2Nombre ? buscarUno(coach2Nombre, directorio) : null
  };
}

// src/rules/disponibilidad.ts
var DIAS_SEMANA = [
  "Domingo",
  "Lunes",
  "Martes",
  "Mi\xE9rcoles",
  "Jueves",
  "Viernes",
  "S\xE1bado"
];
function diaYHoraLocal(ms) {
  const local = new Date(ms + OFFSET_MEXICO_HORAS * 60 * 60 * 1e3);
  const hh = String(local.getUTCHours()).padStart(2, "0");
  const mm = String(local.getUTCMinutes()).padStart(2, "0");
  return { dia: local.getUTCDay(), hora: `${hh}:${mm}` };
}
function tieneDisponibilidadDeclarada(disp) {
  if (!disp) return false;
  return Object.values(disp).some((bloques) => Array.isArray(bloques) && bloques.length > 0);
}
function estaDisponible(disp, inicioMs) {
  if (!tieneDisponibilidadDeclarada(disp)) return true;
  const { dia, hora } = diaYHoraLocal(inicioMs);
  const bloques = disp?.[String(dia)] ?? [];
  return bloques.some((b) => hora >= b.inicio && hora <= b.fin);
}
var fin = (t) => t.inicio_ms + t.duracion_minutos * 6e4;
function buscarChoque(agenda, candidata) {
  if (!candidata.coach_id) return null;
  const finCandidata = fin(candidata);
  return agenda.find(
    (t) => t.coach_id === candidata.coach_id && t.clase_id !== candidata.clase_id && t.inicio_ms < finCandidata && candidata.inicio_ms < fin(t)
  ) ?? null;
}
function bloqueEsValido(b) {
  const patron = /^([01]\d|2[0-3]):[0-5]\d$/;
  return patron.test(b.inicio) && patron.test(b.fin) && b.inicio < b.fin;
}
function normalizarDisponibilidad(disp) {
  const limpia = {};
  for (let dia = 0; dia <= 6; dia++) {
    const bloques = (disp[String(dia)] ?? []).filter(bloqueEsValido).sort((a, b) => a.inicio.localeCompare(b.inicio));
    if (bloques.length > 0) limpia[String(dia)] = bloques;
  }
  return limpia;
}

// src/rules/enlaces.ts
var MODOS_ACCION = ["resetPassword", "verifyEmail", "recoverEmail"];
function esModoAccion(v) {
  return typeof v === "string" && MODOS_ACCION.includes(v);
}
function parametros(enlace) {
  const inicio = enlace.indexOf("?");
  if (inicio === -1) return {};
  const salida = {};
  for (const par of enlace.slice(inicio + 1).split("&")) {
    if (!par) continue;
    const corte = par.indexOf("=");
    const clave = corte === -1 ? par : par.slice(0, corte);
    const valor = corte === -1 ? "" : par.slice(corte + 1);
    try {
      salida[decodeURIComponent(clave)] = decodeURIComponent(valor);
    } catch {
    }
  }
  return salida;
}
function reescribirAccion(enlaceFirebase, base) {
  const p = parametros(enlaceFirebase);
  const modo = p["mode"];
  const codigo = p["oobCode"];
  if (!esModoAccion(modo) || !codigo) return enlaceFirebase;
  const raiz = base.replace(/\/+$/, "");
  return `${raiz}/accion?modo=${encodeURIComponent(modo)}&codigo=${encodeURIComponent(codigo)}`;
}
function leerAccion(consulta) {
  const modo = consulta["modo"] ?? consulta["mode"];
  const codigo = consulta["codigo"] ?? consulta["oobCode"];
  if (!esModoAccion(modo) || !codigo) return null;
  return { modo, codigo };
}

// src/rules/perfil.ts
function resolverNombre(entrada) {
  const nombreExplicito = (entrada.nombre ?? "").trim();
  if (nombreExplicito) {
    return { nombre: nombreExplicito, apellido: (entrada.apellido ?? "").trim() };
  }
  const completo = (entrada.displayName ?? "").trim().replace(/\s+/g, " ");
  if (!completo) return { nombre: "", apellido: "" };
  const [primero = "", ...resto] = completo.split(" ");
  return { nombre: primero, apellido: resto.join(" ") };
}
function perfilSinNombre(perfil) {
  return `${perfil.nombre ?? ""}${perfil.apellido ?? ""}`.trim() === "";
}

// src/rules/modalidades.ts
var APOSTROFOS = /['`´ʼ‘’]/g;
var PICTOGRAMAS = /[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2B00}-\u{2BFF}\u{FE0F}]/gu;
var COMBINANTES = /[̀-ͯ]/g;
function claveModalidad(nombre) {
  return nombre.replace(APOSTROFOS, "'").replace(PICTOGRAMAS, "").normalize("NFD").replace(COMBINANTES, "").toLowerCase().replace(/\s+/g, " ").trim();
}
function esDisciplina(v) {
  return typeof v === "string" && DISCIPLINAS.includes(v);
}
function canonizarModalidad(nombre, catalogo, disciplina) {
  const clave = claveModalidad(nombre);
  if (!clave) return null;
  const candidatas = catalogo.filter(
    (m) => (!disciplina || m.disciplina === disciplina) && (claveModalidad(m.nombre) === clave || m.alias.some((a) => claveModalidad(a) === clave))
  );
  return candidatas.length === 1 ? candidatas[0] : null;
}
function catalogoParaPlantilla(modalidades, coaches) {
  const porDisciplina = {};
  const usos = new Map(modalidades.map((m) => [`${m.disciplina}|${m.nombre}`, m.veces]));
  for (const m of modalidades) {
    if (!m.activa || m.por_revisar) continue;
    (porDisciplina[m.disciplina] ??= []).push(m.nombre);
  }
  for (const d of Object.keys(porDisciplina)) {
    porDisciplina[d].sort((a, b) => {
      const ua = usos.get(`${d}|${a}`) ?? 0;
      const ub = usos.get(`${d}|${b}`) ?? 0;
      return ub - ua || (a < b ? -1 : a > b ? 1 : 0);
    });
  }
  return { coaches, modalidades: porDisciplina };
}
var SEPARADOR_ETIQUETA = " \xB7 ";
function etiquetaModalidad(m) {
  return `${m.disciplina}${SEPARADOR_ETIQUETA}${m.nombre}`;
}
function leerEtiquetaModalidad(texto, catalogo) {
  const limpio = texto.trim();
  if (!limpio) return null;
  const corte = limpio.indexOf(SEPARADOR_ETIQUETA.trim());
  if (corte > 0) {
    const posible = limpio.slice(0, corte).trim().toUpperCase();
    if (esDisciplina(posible)) {
      const m2 = canonizarModalidad(limpio.slice(corte + 1), catalogo, posible);
      return m2 ? { disciplina: m2.disciplina, nombre: m2.nombre } : null;
    }
  }
  const m = canonizarModalidad(limpio, catalogo);
  return m ? { disciplina: m.disciplina, nombre: m.nombre } : null;
}
function fusionarVariantes(vistas) {
  const grupos = /* @__PURE__ */ new Map();
  for (const v of vistas) {
    const nombre = v.nombre.trim();
    const clave = claveModalidad(nombre);
    if (!clave) continue;
    const llave = `${v.disciplina}|${clave}`;
    const grupo = grupos.get(llave) ?? [];
    grupo.push({ ...v, nombre });
    grupos.set(llave, grupo);
  }
  const salida = [];
  for (const grupo of grupos.values()) {
    const ordenadas = [...grupo].sort(
      (a, b) => b.veces - a.veces || (a.nombre < b.nombre ? -1 : a.nombre > b.nombre ? 1 : 0)
    );
    const ganadora = ordenadas[0];
    salida.push({
      disciplina: ganadora.disciplina,
      canonica: ganadora.nombre,
      alias: ordenadas.slice(1).map((v) => v.nombre),
      veces: ordenadas.reduce((t, v) => t + v.veces, 0)
    });
  }
  return salida.sort(
    (a, b) => a.disciplina.localeCompare(b.disciplina) || b.veces - a.veces || (a.canonica < b.canonica ? -1 : a.canonica > b.canonica ? 1 : 0)
  );
}
function conciliarCatalogo(catalogo, vistas) {
  const fusionadas = fusionarVariantes(vistas);
  const nuevas = [];
  const variantes = [];
  const usoPorEntrada = /* @__PURE__ */ new Map();
  for (const f of fusionadas) {
    const existente = canonizarModalidad(f.canonica, catalogo, f.disciplina);
    if (!existente) {
      nuevas.push({
        disciplina: f.disciplina,
        nombre: f.canonica,
        /* Activa porque la clase ya está publicada; fingir lo contrario no la
           despublica. `por_revisar` la mantiene fuera de la planeación. */
        activa: true,
        alias: f.alias,
        veces: f.veces,
        por_revisar: true
      });
      continue;
    }
    const clavePropia = `${existente.disciplina}|${existente.nombre}`;
    usoPorEntrada.set(clavePropia, (usoPorEntrada.get(clavePropia) ?? 0) + f.veces);
    if (claveModalidad(existente.nombre) !== claveModalidad(f.canonica)) {
      variantes.push({
        disciplina: f.disciplina,
        canonica: existente.nombre,
        visto: f.canonica,
        veces: f.veces
      });
    }
    for (const a of f.alias) {
      if (!existente.alias.some((x) => claveModalidad(x) === claveModalidad(a))) {
        variantes.push({
          disciplina: f.disciplina,
          canonica: existente.nombre,
          visto: a,
          veces: 0
        });
      }
    }
  }
  const actualizado = catalogo.map((m) => ({
    ...m,
    veces: usoPorEntrada.get(`${m.disciplina}|${m.nombre}`) ?? 0
  }));
  return {
    nuevas,
    variantes,
    sin_uso: actualizado.filter((m) => m.veces === 0),
    actualizado: [...actualizado, ...nuevas]
  };
}

// src/rules/plantilla.ts
var HORARIOS_ESTANDAR = {
  ZYCLING: ["06:05", "08:05", "09:05", "10:10", "11:10", "18:05", "19:05", "20:05"],
  ZTRENGTH: ["07:10", "08:10", "09:10", "10:10", "18:10", "19:10", "20:10"]
};
var DISCIPLINAS_DE_HOJA = {
  ZYCLING: ["ZYCLING", "ESPECIAL"],
  ZTRENGTH: ["ZTRENGTH", "ZCULPT", "ZYNC", "ZAVAGE", "ESPECIAL"]
};
var DIAS_PLANTILLA = [
  "LUNES",
  "MARTES",
  "MI\xC9RCOLES",
  "JUEVES",
  "VIERNES",
  "S\xC1BADO",
  "DOMINGO"
];
var normalizar = claveModalidad;
function instanteDeCelda(lunesMs, dia, hora) {
  const [h, m] = hora.split(":").map(Number);
  return lunesMs + dia * MS_POR_DIA + (h ?? 0) * 36e5 + (m ?? 0) * 6e4;
}
function interpretarPlantilla(celdas, catalogo, lunesMs) {
  const clases = [];
  const errores = [];
  const avisos = [];
  const buscarCoach = (nombre) => catalogo.coaches.find((c) => normalizar(c) === normalizar(nombre)) ?? null;
  for (const c of celdas) {
    const modalidad = c.modalidad.trim();
    const coach = c.coach.trim();
    const resumen = [c.disciplina, modalidad, coach].filter(Boolean).join(" \xB7 ") || "(vac\xEDa)";
    if (!modalidad && !coach) continue;
    if (!modalidad || !coach) {
      errores.push({
        dia: c.dia,
        hora: c.hora,
        celda: resumen,
        motivo: !modalidad ? "falta la modalidad" : "falta el coach"
      });
      continue;
    }
    const disciplina = c.disciplina.trim().toUpperCase();
    if (!esDisciplina(disciplina)) {
      errores.push({ dia: c.dia, hora: c.hora, celda: resumen, motivo: `disciplina desconocida: ${c.disciplina}` });
      continue;
    }
    const permitidas = catalogo.modalidades[disciplina];
    if (!permitidas || permitidas.length === 0) {
      errores.push({
        dia: c.dia,
        hora: c.hora,
        celda: resumen,
        motivo: `no hay modalidades cargadas para ${disciplina}; revisa el cat\xE1logo`
      });
      continue;
    }
    const modalidadCanonica = permitidas.find((m) => normalizar(m) === normalizar(modalidad)) ?? modalidad;
    if (modalidadCanonica === modalidad && !permitidas.some((m) => normalizar(m) === normalizar(modalidad))) {
      avisos.push({
        dia: c.dia,
        hora: c.hora,
        celda: resumen,
        motivo: `\xAB${modalidad}\xBB no est\xE1 en el cat\xE1logo de ${disciplina}; se crear\xE1 tal cual`
      });
    }
    const coachCanonico = buscarCoach(coach);
    if (!coachCanonico) {
      errores.push({ dia: c.dia, hora: c.hora, celda: resumen, motivo: `no hay ning\xFAn coach llamado \xAB${coach}\xBB` });
      continue;
    }
    let coach2Canonico = null;
    if (c.coach2 && c.coach2.trim()) {
      coach2Canonico = buscarCoach(c.coach2);
      if (!coach2Canonico) {
        errores.push({
          dia: c.dia,
          hora: c.hora,
          celda: resumen,
          motivo: `no hay ning\xFAn coach llamado \xAB${c.coach2.trim()}\xBB`
        });
        continue;
      }
      if (coach2Canonico === coachCanonico) {
        errores.push({ dia: c.dia, hora: c.hora, celda: resumen, motivo: "el mismo coach aparece dos veces" });
        continue;
      }
    }
    clases.push({
      dia: c.dia,
      hora: c.hora,
      disciplina,
      modalidad: modalidadCanonica,
      coach: coachCanonico,
      coach2: coach2Canonico,
      fecha_hora_ms: instanteDeCelda(lunesMs, c.dia, c.hora)
    });
  }
  return { clases, errores, avisos };
}
function choquesEnPlantilla(clases, duracionMinutos = 50) {
  const choques = [];
  const ordenadas = [...clases].sort((x, y) => x.fecha_hora_ms - y.fecha_hora_ms);
  for (let i = 0; i < ordenadas.length; i++) {
    for (let j = i + 1; j < ordenadas.length; j++) {
      const a = ordenadas[i];
      const b = ordenadas[j];
      if (b.fecha_hora_ms >= a.fecha_hora_ms + duracionMinutos * 6e4) break;
      const deA = [a.coach, a.coach2].filter(Boolean);
      const deB = [b.coach, b.coach2].filter(Boolean);
      const compartido = deA.find((c) => deB.includes(c));
      if (compartido) choques.push({ a, b, coach: compartido });
    }
  }
  return choques;
}

// src/rules/sesion.ts
function tokenSigueVigente(authTimeMs, tokensValidosDesdeMs) {
  if (!tokensValidosDesdeMs) return true;
  return authTimeMs + 1e3 >= tokensValidosDesdeMs;
}
function authTimeAMs(authTimeSegundos) {
  return typeof authTimeSegundos === "number" ? authTimeSegundos * 1e3 : 0;
}
function validarCambioRol(p) {
  if (p.rol_actual === p.rol_nuevo) {
    return { ok: false, codigo: ERR.ROL_SIN_CAMBIO };
  }
  if (p.actor_uid === p.objetivo_uid) {
    return { ok: false, codigo: ERR.NO_PUEDE_CAMBIAR_SU_ROL };
  }
  if (p.rol_actual === "admin" && p.rol_nuevo !== "admin" && p.admins_totales <= 1) {
    return { ok: false, codigo: ERR.ULTIMO_ADMIN };
  }
  return { ok: true };
}
function esDegradacion(rolActual, rolNuevo) {
  const nivel = { alumno: 0, coach: 1, recepcion: 2, admin: 3 };
  return nivel[rolNuevo] < nivel[rolActual];
}

// src/rules/tienda.ts
var GRUPOS_TIENDA = ["empezar", "clases", "ilimitados"];
var ETIQUETA_GRUPO = {
  empezar: "Para empezar",
  clases: "Paquetes de clases",
  ilimitados: "Ilimitados"
};
var DESCRIPCION_GRUPO = {
  empezar: "Si es tu primera vez o quieres probar antes de decidir.",
  clases: "Cr\xE9ditos que usas cuando quieras, dentro de su vigencia.",
  ilimitados: "Ven todas las veces que quieras. Incluyen pases para invitar."
};
function grupoDePaquete(p) {
  if (p.tipo_credito === "bday_deal") return null;
  if (esUnlimited(p.tipo_credito)) return "ilimitados";
  if (p.solo_primera_vez || p.total_clases <= 2) return "empezar";
  return "clases";
}
function agruparCatalogo(paquetes) {
  return GRUPOS_TIENDA.map((grupo) => ({
    grupo,
    paquetes: paquetes.filter((p) => grupoDePaquete(p) === grupo).sort((a, b) => a.total_clases - b.total_clases || a.precio_centavos - b.precio_centavos)
  })).filter((g) => g.paquetes.length > 0);
}

// src/rules/diagnostico.ts
var EVENTO = {
  /** La pasarela rechazó la tarjeta. Lo normal, pero hay que poder contarlo. */
  PAGO_RECHAZADO: "pago_rechazado",
  /** Falla del navegador contra la pasarela: red, configuración, guion bloqueado. */
  PAGO_ERROR_CLIENTE: "pago_error_cliente",
  /**
   * Cobrado y sin acreditar. **El caso caro.**
   *
   * La pasarela cobró y el cierre contra nuestro servidor falló. La persona ya
   * pagó y no tiene su paquete. Hasta ahora esto solo se sabía si ella escribía.
   */
  PAGO_NO_ACREDITADO: "pago_no_acreditado",
  /** No se pudo pedir el permiso para mostrar las tarjetas guardadas. */
  SESION_PAGO_FALLIDA: "sesion_pago_fallida",
  /** El perfil no pudo leer sus medios de pago. */
  MEDIOS_PAGO_ERROR: "medios_pago_error",
  /** Administración no pudo consultar quién tiene cuenta de acceso. */
  ZTAFF_ERROR: "ztaff_error"
};
var TIPOS_EVENTO = Object.values(EVENTO);
function esTipoEvento(v) {
  return typeof v === "string" && TIPOS_EVENTO.includes(v);
}
var LARGO_MAXIMO_DETALLE = 500;
var MAXIMO_CLAVES_CONTEXTO = 12;
var LARGO_MAXIMO_VALOR = 200;
var CORREO = /[\w.+-]+@[\w-]+\.[\w.-]+/g;
var SECRETO_PASARELA = /(_secret_)[A-Za-z0-9_-]+/g;
var LLAVE_SECRETA = /\b(sk|rk)_(live|test)_[A-Za-z0-9]+/g;
var TOKEN_JWT = /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;
var POSIBLE_TARJETA = /\b(?:\d[ -]?){12,18}\d\b/g;
function pasaLuhn(digitos) {
  if (!/^\d{13,19}$/.test(digitos)) return false;
  let suma = 0;
  let doblar = false;
  for (let i = digitos.length - 1; i >= 0; i--) {
    let d = digitos.charCodeAt(i) - 48;
    if (doblar) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    suma += d;
    doblar = !doblar;
  }
  return suma % 10 === 0;
}
function depurarDiagnostico(texto, largoMaximo = LARGO_MAXIMO_DETALLE) {
  const limpio = String(texto ?? "").replace(TOKEN_JWT, "\xABtoken\xBB").replace(LLAVE_SECRETA, "\xABllave\xBB").replace(SECRETO_PASARELA, "$1\xABoculto\xBB").replace(CORREO, "\xABcorreo\xBB").replace(POSIBLE_TARJETA, (m) => pasaLuhn(m.replace(/[ -]/g, "")) ? "\xABtarjeta\xBB" : m).replace(/\s+/g, " ").trim();
  return limpio.length > largoMaximo ? limpio.slice(0, largoMaximo - 1) + "\u2026" : limpio;
}
function depurarContexto(contexto) {
  const salida = {};
  for (const clave of Object.keys(contexto ?? {}).slice(0, MAXIMO_CLAVES_CONTEXTO)) {
    const k = depurarDiagnostico(clave, 40);
    if (!k) continue;
    const v = contexto[clave];
    const bruto = typeof v === "object" && v !== null ? JSON.stringify(v) : String(v);
    salida[k] = depurarDiagnostico(bruto, LARGO_MAXIMO_VALOR);
  }
  return salida;
}

// src/rules/analitica.ts
var ACCION = {
  // ── Tienda: nombres estándar de comercio ──────────────────────────────
  VER_CATALOGO: "view_item_list",
  AGREGAR_AL_CARRITO: "add_to_cart",
  QUITAR_DEL_CARRITO: "remove_from_cart",
  INICIAR_PAGO: "begin_checkout",
  COMPRA: "purchase",
  // ── Lo que no es comercio, en español ─────────────────────────────────
  CUPON_INTENTADO: "cupon_intentado",
  RESERVA_CREADA: "reserva_creada",
  RESERVA_CANCELADA: "reserva_cancelada",
  LUGAR_CAMBIADO: "reserva_lugar_cambiado",
  INVITADO_AGREGADO: "invitado_agregado",
  MEDIO_PAGO_GUARDADO: "medio_pago_guardado",
  // ── Cuenta: `sign_up` y `login` también son estándar ───────────────────
  REGISTRO: "sign_up",
  ACCESO: "login",
  CORREO_VERIFICADO: "correo_verificado"
};
var CATALOGO_ACCIONES = {
  [ACCION.VER_CATALOGO]: {
    nombre: ACCION.VER_CATALOGO,
    origen: "cliente",
    para: "Cu\xE1nta gente llega a ver paquetes, para medir contra cu\xE1nta compra"
  },
  /*
   * Falta `view_item`, y no por descuido.
   *
   * Es el estándar para «miró este producto», pero la tienda muestra las
   * tarjetas en lista y no tiene pantalla de detalle: no hay ningún momento en
   * que alguien mire *un* paquete. Declararlo sin poder emitirlo dejaría una
   * entrada muerta en el catálogo y un hueco en el embudo que parecería un fallo
   * de medición. Entra el día que exista esa pantalla.
   */
  [ACCION.AGREGAR_AL_CARRITO]: {
    nombre: ACCION.AGREGAR_AL_CARRITO,
    origen: "cliente",
    para: "Primer paso del embudo de compra"
  },
  [ACCION.QUITAR_DEL_CARRITO]: {
    nombre: ACCION.QUITAR_DEL_CARRITO,
    origen: "cliente",
    para: "D\xF3nde se arrepiente la gente antes de pagar"
  },
  [ACCION.INICIAR_PAGO]: {
    nombre: ACCION.INICIAR_PAGO,
    origen: "cliente",
    para: "Cu\xE1ntos llegan a la tarjeta. La ca\xEDda de aqu\xED a `purchase` es el abandono"
  },
  [ACCION.COMPRA]: {
    nombre: ACCION.COMPRA,
    origen: "servidor",
    para: "Ventas. Del servidor porque el navegador puede morir tras pagar"
  },
  [ACCION.CUPON_INTENTADO]: {
    nombre: ACCION.CUPON_INTENTADO,
    origen: "cliente",
    para: "Si las campa\xF1as se usan, y cu\xE1ntos c\xF3digos se teclean mal"
  },
  [ACCION.RESERVA_CREADA]: {
    nombre: ACCION.RESERVA_CREADA,
    origen: "servidor",
    para: "Ocupaci\xF3n real por disciplina y horario"
  },
  [ACCION.RESERVA_CANCELADA]: {
    nombre: ACCION.RESERVA_CANCELADA,
    origen: "servidor",
    para: "Cu\xE1nto se cancela y con cu\xE1nta antelaci\xF3n, que es lo que decide la multa"
  },
  [ACCION.LUGAR_CAMBIADO]: {
    nombre: ACCION.LUGAR_CAMBIADO,
    origen: "cliente",
    para: "Si el mapa de lugares se usa o la gente acepta el que le toca"
  },
  [ACCION.INVITADO_AGREGADO]: {
    nombre: ACCION.INVITADO_AGREGADO,
    origen: "servidor",
    para: "Cu\xE1nto se usa el pase +1, que es el canal de clientas nuevas"
  },
  [ACCION.MEDIO_PAGO_GUARDADO]: {
    nombre: ACCION.MEDIO_PAGO_GUARDADO,
    origen: "cliente",
    para: "Cu\xE1ntas tarjetas quedan guardadas, requisito para cobrar suscripciones"
  },
  [ACCION.REGISTRO]: {
    nombre: ACCION.REGISTRO,
    origen: "cliente",
    para: "Altas nuevas y por qu\xE9 m\xE9todo entraron"
  },
  [ACCION.ACCESO]: {
    nombre: ACCION.ACCESO,
    origen: "cliente",
    para: "Personas activas, para distinguir quien vuelve de quien se registr\xF3 y no volvi\xF3"
  },
  [ACCION.CORREO_VERIFICADO]: {
    nombre: ACCION.CORREO_VERIFICADO,
    origen: "cliente",
    para: "Cu\xE1ntas altas se quedan a medias sin confirmar el correo"
  }
};
var LIMITE = {
  NOMBRE: 40,
  CLAVE_PARAMETRO: 40,
  VALOR_TEXTO: 100,
  PARAMETROS: 25,
  ARTICULOS: 10
};
var PREFIJOS_RESERVADOS = ["firebase_", "google_", "ga_"];
var SNAKE_CASE = /^[a-z][a-z0-9_]*$/;
var CORREO2 = /[\w.+-]+@[\w-]+\.[\w.-]+/;
var TELEFONO = /(?:\+?\d[ ()-]?){10,}/;
var CLAVES_PROHIBIDAS = /(email|correo|telefono|phone|nombre|apellido|nacimiento|birth|direccion|address|password|token|secret)/i;
function aPesos(centavos) {
  return Math.round(centavos) / 100;
}
function prepararAccion(nombre, parametros2 = {}, articulos) {
  const problemas = [];
  if (!(nombre in CATALOGO_ACCIONES)) {
    return { accion: null, problemas: [`\xAB${nombre}\xBB no est\xE1 en el cat\xE1logo de acciones`] };
  }
  const limpios = {};
  let contados = 0;
  for (const [clave, valor] of Object.entries(parametros2)) {
    if (contados >= LIMITE.PARAMETROS) {
      problemas.push(`se pas\xF3 de ${LIMITE.PARAMETROS} par\xE1metros; sobra \xAB${clave}\xBB`);
      continue;
    }
    if (!SNAKE_CASE.test(clave) || clave.length > LIMITE.CLAVE_PARAMETRO) {
      problemas.push(`\xAB${clave}\xBB no es snake_case de ${LIMITE.CLAVE_PARAMETRO} o menos`);
      continue;
    }
    if (PREFIJOS_RESERVADOS.some((p) => clave.startsWith(p))) {
      problemas.push(`\xAB${clave}\xBB usa un prefijo reservado por el destino`);
      continue;
    }
    if (CLAVES_PROHIBIDAS.test(clave)) {
      problemas.push(`\xAB${clave}\xBB nombra un dato personal y la anal\xEDtica no lo guarda`);
      continue;
    }
    if (typeof valor === "number") {
      if (!Number.isFinite(valor)) {
        problemas.push(`\xAB${clave}\xBB no es un n\xFAmero finito`);
        continue;
      }
      limpios[clave] = valor;
      contados++;
      continue;
    }
    if (typeof valor === "boolean") {
      limpios[clave] = valor;
      contados++;
      continue;
    }
    if (typeof valor !== "string") {
      problemas.push(`\xAB${clave}\xBB no es texto, n\xFAmero ni booleano`);
      continue;
    }
    if (CORREO2.test(valor) || TELEFONO.test(valor)) {
      problemas.push(`\xAB${clave}\xBB contiene un dato personal y se descarta`);
      continue;
    }
    if (valor.length > LIMITE.VALOR_TEXTO) {
      problemas.push(`\xAB${clave}\xBB pasa de ${LIMITE.VALOR_TEXTO} caracteres y el destino lo cortar\xEDa`);
      continue;
    }
    limpios[clave] = valor;
    contados++;
  }
  let lista;
  if (articulos?.length) {
    if (articulos.length > LIMITE.ARTICULOS) {
      problemas.push(`se mandaron ${articulos.length} art\xEDculos; se conservan ${LIMITE.ARTICULOS}`);
    }
    lista = articulos.slice(0, LIMITE.ARTICULOS);
  }
  return { accion: { nombre, parametros: limpios, articulos: lista }, problemas };
}
export {
  ACCION,
  ANTICIPACION_MINIMA_DIAS,
  CATALOGO_ACCIONES,
  COL,
  CONFIG_DOC,
  CONFIG_OPERATIVA_DEFAULT,
  DESCRIPCION_GRUPO,
  DIAS_PLANTILLA,
  DIAS_SEMANA,
  DISCIPLINAS,
  DISCIPLINAS_DE_HOJA,
  ERR,
  ESTADOS_ASISTENCIA,
  ESTADOS_CLASE,
  ESTADOS_COMPRA,
  ESTADOS_RESERVA,
  ESTADOS_USUARIO,
  ETIQUETA_GRUPO,
  EVENTO,
  GRUPOS_TIENDA,
  HORARIOS_ESTANDAR,
  LARGO_MAXIMO_DETALLE,
  LARGO_MAXIMO_VALOR,
  LIMITE,
  LONGITUD_MINIMA_LESION,
  MAXIMO_CLAVES_CONTEXTO,
  MAX_COACHES_BDAY,
  MENSAJES_ERROR,
  MIN_COACHES_BDAY,
  MODOS_ACCION,
  MONEDA,
  MS_POR_DIA,
  MS_POR_HORA,
  OFFSET_MEXICO_HORAS,
  ROLES,
  ROLES_STAFF,
  SALAS_BDAY,
  TIPOS_CREDITO,
  TIPOS_DESCUENTO,
  TIPOS_EVENTO,
  TIPOS_UNLIMITED,
  aPesos,
  accesosStaffSchema,
  agruparCatalogo,
  anioLocal,
  antiguedadLegible,
  antiguedadReporteDias,
  aplicarPorcentaje,
  asegurarPerfilSchema,
  asignarPaqueteSchema,
  authTimeAMs,
  bloqueEsValido,
  buscarChoque,
  buscarCompraConInvitacion,
  buscarLugar,
  calcularCheckout,
  calcularDescuento,
  calcularEdad,
  calcularExpiracionMs,
  cambiarLugarSchema,
  cambiarRolSchema,
  cancelarReservaSchema,
  canonizarModalidad,
  capacidadUtil,
  catalogoParaPlantilla,
  centavosAPesos,
  checkoutSchema,
  choquesEnPlantilla,
  claveModalidad,
  conciliarCatalogo,
  convertirSala,
  convertirSlots,
  crearStaffSchema,
  creditosUtilizables,
  cubreDisciplina,
  cuponSchema,
  datosInvitadoSchema,
  deducirDisciplina,
  depurarContexto,
  depurarDiagnostico,
  descripcionEsUtil,
  devolverInvitacion,
  diaYHoraLocal,
  diasRestantes,
  emailNormalizado,
  esCentavosValidos,
  esDegradacion,
  esDisciplina,
  esItemElegible,
  esMesDeCumpleanos,
  esPlanoReutilizable,
  esPublicable,
  esStaff,
  esTipoEvento,
  esUnlimited,
  estaDisponible,
  estaVigente,
  estadoInvitaciones,
  estatusCalculado,
  etiquetaModalidad,
  evaluarCancelacion,
  evaluarDisponibilidadBday,
  evaluarPenalizacion,
  extraerModalidad,
  fechaMinimaEvento,
  finDeVentanaMs,
  formatearMXN,
  fusionarVariantes,
  generarLayout,
  grupoDePaquete,
  idDeterministaClase,
  idDeterministaSala,
  inicioDeSemanaUTC,
  instanteDeCelda,
  interpretarPlantilla,
  layoutSalaSchema,
  leerAccion,
  leerEtiquetaModalidad,
  limiteCancelacionMs,
  lineaCarritoSchema,
  liquidarMultaSchema,
  lugaresDisponibles,
  marcarAsistenciaSchema,
  mensajeDe,
  mesDeNacimiento,
  mesLocal,
  normalizarDisponibilidad,
  notificacionSchema,
  paqueteSchema,
  pasaLuhn,
  perfilAlumnoSchema,
  perfilSinNombre,
  pesosACentavos,
  prepararAccion,
  proyectarCuponPublico,
  puedeReactivarse,
  reescribirAccion,
  registrarUsoInvitacion,
  reporteEsAntiguo,
  reservarClaseSchema,
  resolverCoaches,
  resolverNombre,
  restarSinNegativo,
  resumirSaldo,
  salaYaUsadaEsteAnio,
  salasDisponibles,
  seleccionarCredito,
  tieneDisponibilidadDeclarada,
  tieneLesionReportada,
  tieneRestricciones,
  tokenSigueVigente,
  validarCambioRol,
  validarCupon,
  validarCuponSchema,
  validarLugar,
  validarLugares,
  validarSolicitudBday,
  ventanaActual,
  ventanasTotales
};
//# sourceMappingURL=index.js.map